import Avatar from '@mui/material/Avatar';
import Button from '@mui/material/Button';
import Collapse from '@mui/material/Collapse';
import Divider from '@mui/material/Divider';
import List from '@mui/material/List';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import MenuItem from '@mui/material/MenuItem';
import Popover from '@mui/material/Popover';
import Typography from '@mui/material/Typography';
import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { communicateWithAPI } from 'app/main/administration/store/absAdministrationSlice';
import { Link, NavLink } from 'react-router-dom';
import ABSSvgIcon from '@abs/core/ABSSvgIcon';
import { selectUser } from 'app/store/userSlice';
import { useTranslation } from 'react-i18next';
import ABSUtils from '@abs/utils';
import { useTheme } from '@mui/material';
import { setDarkMode } from 'app/store/abs/settingsSlice';
import { changeLanguageCode, selectCurrentLanguage, selectLanguages } from 'app/store/i18nSlice';
import history from '@history';

function UserMenu(props) {
  const dispatch = useDispatch();
  const user = useSelector(selectUser);
  const { t } = useTranslation('abslayouts');
  const [userMenu, setUserMenu] = useState(null);
  const [langOpen, setLangOpen] = useState(false);
  const theme = useTheme();
  const currentLanguage = useSelector(selectCurrentLanguage);
  const languages = useSelector(selectLanguages);

  const userMenuClick = (event) => {
    setUserMenu(event.currentTarget);
  };

  const userMenuClose = () => {
    setUserMenu(null);
    setLangOpen(false);
  };

  function handleLanguageChange(lng) {
    dispatch(changeLanguageCode(lng.id));
    userMenuClose();
  }

  return (
    <>
      <Button
        className="min-h-52 min-w-40 px-0 pl-8 md:px-16 py-0 md:py-6"
        onClick={userMenuClick}
        color="inherit"
      >
        <div className="hidden md:flex flex-col mx-4 items-end">
          <Typography component="span" className="font-semibold flex">
            {user.User_Firstname + " " + user.User_Name}
          </Typography>
          <Typography className="text-11 font-medium capitalize" color="text.secondary">
            {(!user.modules || (Array.isArray(user.modules) && user.modules.length === 0)) ?
              t("NOTLOGGEDIN")
              :
              user.CompanyName.toString()
            }
          </Typography>
        </div>

        {user.ProfilePicture ? (
          <Avatar className="md:mx-4" alt="user photo" src={user.ProfilePicture} />
        ) : (
          <Avatar sx={user.Color && { bgcolor: user.Color, color: ABSUtils.invertColor(user.Color, true) }} className="md:mx-4">{ABSUtils.getAvatarFallbackCharArray(user.User_Firstname + " " + user.User_Name)}</Avatar>
        )}
      </Button>

      <Popover
        open={Boolean(userMenu)}
        anchorEl={userMenu}
        onClose={userMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        classes={{
          paper: 'py-8',
        }}
      >
        {!user.modules || user.modules.length === 0 ? (
          <>
            <MenuItem component={Link} to="/login" role="button">
              <ListItemIcon className="min-w-40">
                <ABSSvgIcon>heroicons-outline:lock-closed</ABSSvgIcon>
              </ListItemIcon>
              <ListItemText primary="Login" />
            </MenuItem>
            <MenuItem component={Link} to="/sign-up" role="button">
              <ListItemIcon className="min-w-40">
                <ABSSvgIcon>heroicons-outline:user-add </ABSSvgIcon>
              </ListItemIcon>
              <ListItemText primary="Sign up" />
            </MenuItem>
          </>
        ) : (
          <>
            <MenuItem component={Link} to="/usersettings" onClick={userMenuClose} role="button">
              <ListItemIcon className="min-w-40">
                <ABSSvgIcon>heroicons-outline:user-circle</ABSSvgIcon>
              </ListItemIcon>
              <ListItemText primary={t("USERMENU_PROFILE")} />
            </MenuItem>
            <MenuItem
              onClick={() => {
                setDarkMode(theme.palette.mode == "light" ? true : false);
                window.location.reload();
              }}
            >
              {theme.palette.mode == "light" ?
                <>
                  <ListItemIcon className="min-w-40">
                    <ABSSvgIcon>brightness_2</ABSSvgIcon>
                  </ListItemIcon>
                  <ListItemText primary={t("DARKMODE_ACTIVATE")} />
                </>
                :
                <>
                  <ListItemIcon className="min-w-40">
                    <ABSSvgIcon>wb_sunny</ABSSvgIcon>
                  </ListItemIcon>
                  <ListItemText primary={t("DARKMODE_DEACTIVATE")} />
                </>
              }
            </MenuItem>
            <Divider />
            <MenuItem onClick={() => setLangOpen((prev) => !prev)}>
              <ListItemIcon className="min-w-40">
                <img
                  className="w-24"
                  src={`assets/images/flags/${currentLanguage.flag}.svg`}
                  alt={currentLanguage.title}
                />
              </ListItemIcon>
              <ListItemText primary={currentLanguage.title} />
              <ABSSvgIcon className="ml-8 text-16">
                {langOpen ? 'heroicons-outline:chevron-up' : 'heroicons-outline:chevron-down'}
              </ABSSvgIcon>
            </MenuItem>
            <Collapse in={langOpen} timeout="auto" unmountOnExit>
              <List disablePadding>
                {languages.map((lng) => (
                  <MenuItem
                    key={lng.id}
                    selected={lng.id === currentLanguage.id}
                    onClick={() => handleLanguageChange(lng)}
                    sx={{ pl: 4 }}
                  >
                    <ListItemIcon className="min-w-40">
                      <img
                        className="w-24"
                        src={`assets/images/flags/${lng.flag}.svg`}
                        alt={lng.title}
                      />
                    </ListItemIcon>
                    <ListItemText primary={lng.title} />
                  </MenuItem>
                ))}
              </List>
            </Collapse>
            <Divider />
            <MenuItem
              onClick={() => {
                window.location.reload();
              }}
            >
              <ListItemIcon className="min-w-40">
                <ABSSvgIcon>heroicons-outline:refresh</ABSSvgIcon>
              </ListItemIcon>
              <ListItemText primary={t("USERMENU_RELOAD")} />
            </MenuItem>
            {user.is_impersonating &&
              <MenuItem
                onClick={async () => {
                  userMenuClose();
                  const result = await dispatch(communicateWithAPI("abs_stop_impersonation", null, false));
                  if (result !== "error") {
                    window.location.href = '/administration/user';
                  }
                }}
              >
                <ListItemIcon className="min-w-40">
                  <ABSSvgIcon>heroicons-outline:user-remove</ABSSvgIcon>
                </ListItemIcon>
                <ListItemText primary={t("USERMENU_STOP_IMPERSONATION")} />
              </MenuItem>
            }
            <MenuItem
              component={NavLink}
              to="/logout"
              onClick={() => {
                userMenuClose();
              }}
            >
              <ListItemIcon className="min-w-40">
                <ABSSvgIcon>heroicons-outline:logout</ABSSvgIcon>
              </ListItemIcon>
              <ListItemText primary={t("USERMENU_LOGOUT")} />
            </MenuItem>
          </>
        )}

      </Popover>
    </>
  );
}

export default UserMenu;
